import type { Resolver } from "./types";

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; message: string };

const HOSTNAME_PATTERN =
  /^(?=.{1,253}$)(?!-)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

export function normalizeHostname(input: string): ValidationResult<string> {
  const trimmed = input.trim();
  if (!trimmed) return { ok: false, message: "Enter a hostname." };

  let candidate = trimmed;
  try {
    const withScheme = /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    candidate = new URL(withScheme).hostname;
  } catch {
    candidate = trimmed.split("/")[0] ?? trimmed;
  }

  const normalized = candidate.toLowerCase().replace(/\.$/, "");
  if (!HOSTNAME_PATTERN.test(normalized)) {
    return { ok: false, message: "Enter a valid hostname such as example.com." };
  }
  return { ok: true, value: normalized };
}

export function normalizeHostnames(values: string[]): ValidationResult<string[]> {
  const normalized: string[] = [];
  for (const value of values) {
    const result = normalizeHostname(value);
    if (!result.ok) return result;
    if (!normalized.includes(result.value)) normalized.push(result.value);
  }
  if (normalized.length === 0) return { ok: false, message: "Add at least one test domain." };
  return { ok: true, value: normalized };
}

export function validateEndpoint(input: string): ValidationResult<string> {
  const trimmed = input.trim();
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return { ok: false, message: "Enter a well-formed HTTPS URL." };
  }
  if (url.protocol !== "https:") {
    return { ok: false, message: "DoH endpoints must use https://." };
  }
  if (!url.hostname.includes(".")) {
    return { ok: false, message: "The endpoint must include a valid hostname." };
  }
  return { ok: true, value: url.toString() };
}

export function validateResolver(resolver: Resolver): ValidationResult<Resolver> {
  const endpoint = validateEndpoint(resolver.endpoint);
  if (!endpoint.ok) return endpoint;
  if (!resolver.name.trim()) return { ok: false, message: "Resolver name is required." };
  return {
    ok: true,
    value: {
      ...resolver,
      name: resolver.name.trim(),
      endpoint: endpoint.value
    }
  };
}
